import { useState, useEffect, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { startOfISOWeek, endOfISOWeek, format, parseISO, isValid } from "date-fns";
import API from "../api";
import { BASE_URL } from "../config";
import { getAuthItem } from "../utils/auth";
import { showError } from "../utils/notifications";
import { formatLegacyScheduleDurationText } from "../utils/scheduleDurationUtils";
import {
  formatAppIntlLabel,
  formatAppTime,
  parseDbDateTime,
} from "../utils/appDateTime";
import StaffAvatarCircle from "./StaffAvatarCircle";
import AssignToSimilarDateRangeModal from "./AssignToSimilarDateRangeModal";

function parseShiftDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = parseISO(raw);
    return isValid(d) ? d : null;
  }
  const normalized =
    raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
  const d = new Date(normalized);
  return isValid(d) ? d : null;
}

function isoWeekRangeForShift(shift) {
  const start =
    parseShiftDate(shift?.start_date || shift?.startDate) || new Date();
  const end = parseShiftDate(shift?.end_date || shift?.endDate) || start;
  const anchor = start || end;
  return {
    from: format(startOfISOWeek(anchor), "yyyy-MM-dd"),
    to: format(endOfISOWeek(end), "yyyy-MM-dd"),
  };
}

function formatDisplayRange(start, end) {
  const s = parseDbDateTime(start);
  const e = parseDbDateTime(end);
  if (!s || !e) return `${start || ""} – ${end || ""}`;
  const startStr = `${formatAppIntlLabel(s, { month: "short", day: "numeric" })} · ${formatAppTime(s)}`;
  const endTime = formatAppTime(e);
  return `${startStr} – ${endTime}`;
}

function formatMoney(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `$${n.toFixed(2)}`;
}

function sanitizePhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/[^0-9]/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 ${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+1 ${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

function getShiftTimeRange(row) {
  const start = parseShiftDate(
    row?.start_date ||
      row?.startDate ||
      row?.startDateIso ||
      row?.start_date_iso ||
      row?.scheduledStart ||
      row?.scheduled_start,
  );
  const end = parseShiftDate(
    row?.end_date ||
      row?.endDate ||
      row?.endDateIso ||
      row?.end_date_iso ||
      row?.scheduledEnd ||
      row?.scheduled_end,
  );
  if (!start || !end) return null;
  return {
    start: start.getTime(),
    end: end.getTime(),
  };
}

function shiftsOverlap(a, b) {
  const first = getShiftTimeRange(a);
  const second = getShiftTimeRange(b);
  if (!first || !second) return false;
  return first.start < second.end && second.start < first.end;
}

function isCapacityBlockedShift(row) {
  return row?.is_at_capacity === true || row?.isAtCapacity === true;
}

// Staff already has another shift at this time (any client/location) — the
// assign endpoint would reject it with SHIFT_OVERLAP.
function isBookedElsewhereShift(row) {
  return (
    row?.is_overlapping_original_account === true ||
    row?.isOverlappingOriginalAccount === true
  );
}

function getAuthHeaders() {
  const token = getAuthItem("token");
  const headers = {};
  if (token && token !== "null" && token !== "undefined" && token.trim()) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export default function AssignToSimilarModal({
  open,
  onClose,
  originalShift,
  staffAccountId,
  onDone,
}) {
  const shift = originalShift || {};
  const clientId = shift.client_id ?? shift.clientId ?? null;
  const positionId = shift.position_id ?? shift.positionId ?? null;
  const originalAccountId = staffAccountId ? Number(staffAccountId) : null;

  const defaultWeek = useMemo(() => isoWeekRangeForShift(shift), [shift]);

  const [rangeFrom, setRangeFrom] = useState(defaultWeek.from);
  const [rangeTo, setRangeTo] = useState(defaultWeek.to);
  const [shifts, setShifts] = useState([]);
  const [initialShifts, setInitialShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rowBusyId, setRowBusyId] = useState(null);
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [worker, setWorker] = useState(null);

  const clientName =
    shift.client_name ||
    shift.clientName ||
    shift.client ||
    "";
  const positionName =
    shift.position_name ||
    shift.positionName ||
    shift.position ||
    shift.role ||
    "";
  const employeeName = worker
    ? `${worker.first_name || ""} ${worker.last_name || ""}`.trim()
    : shift.assigned_name || shift.staffDisplay || "Staff";
  const supervisorName =
    shift.supervisor_name ||
    shift.supervisorName ||
    (shift.supervisorContactAccount
      ? `${shift.supervisorContactAccount.firstName || ""} ${shift.supervisorContactAccount.lastName || ""}`.trim()
      : null);
  const payRate = shift.pay_rate ?? shift.payRate ?? null;
  const billRate = shift.bill_rate ?? shift.billRate ?? null;
  const durationText = formatLegacyScheduleDurationText(shift);
  const visibleShifts = useMemo(() => {
    const isAssignedToOriginal = (row) => {
      const currentAccountId =
        row?.account_id != null ? Number(row.account_id) : null;
      return !!row.assigned && currentAccountId === originalAccountId;
    };

    const selectableShifts = shifts.filter((row) => {
      const currentAccountId =
        row?.account_id != null ? Number(row.account_id) : null;
      if (isAssignedToOriginal(row)) return true;
      if (isCapacityBlockedShift(row)) return false;
      if (isBookedElsewhereShift(row)) return false;
      return !row.assigned || currentAccountId === originalAccountId;
    });

    const selectedShifts = selectableShifts.filter(isAssignedToOriginal);

    return selectableShifts.filter((row) => {
      if (isAssignedToOriginal(row)) return true;
      return !selectedShifts.some((selected) => shiftsOverlap(row, selected));
    });
  }, [shifts, originalAccountId]);
  const hiddenOtherStaffCount = useMemo(() => {
    return shifts.filter((row) => {
      const currentAccountId =
        row?.account_id != null ? Number(row.account_id) : null;
      return !!row.assigned && currentAccountId !== originalAccountId;
    }).length;
  }, [shifts, originalAccountId]);
  const hiddenCapacityCount = useMemo(() => {
    return shifts.filter((row) => {
      const currentAccountId =
        row?.account_id != null ? Number(row.account_id) : null;
      const assignedToOriginal = row.assigned && currentAccountId === originalAccountId;
      return !assignedToOriginal && isCapacityBlockedShift(row);
    }).length;
  }, [shifts, originalAccountId]);
  const hiddenBookedElsewhereCount = useMemo(() => {
    return shifts.filter((row) => {
      if (row.assigned) return false;
      if (isCapacityBlockedShift(row)) return false;
      return isBookedElsewhereShift(row);
    }).length;
  }, [shifts]);
  const hiddenOverlapCount = useMemo(() => {
    const visibleIds = new Set(visibleShifts.map((row) => row.shift_id));
    return shifts.filter((row) => {
      if (visibleIds.has(row.shift_id)) return false;
      const currentAccountId =
        row?.account_id != null ? Number(row.account_id) : null;
      if (row.assigned && currentAccountId !== originalAccountId) {
        return false;
      }
      if (isCapacityBlockedShift(row)) {
        return false;
      }
      if (isBookedElsewhereShift(row)) {
        return false;
      }
      return true;
    }).length;
  }, [shifts, visibleShifts, originalAccountId]);

  const loadShifts = useCallback(async () => {
    if (!clientId || !positionId || !rangeFrom || !rangeTo || !originalAccountId) {
      setShifts([]);
      return;
    }
    setLoading(true);
    try {
      const resp = await API.get("/api/jobs/assign-to-similar/shifts", {
        params: {
          client_id: clientId,
          position_id: positionId,
          date_from: rangeFrom,
          date_to: rangeTo,
          original_account_id: originalAccountId,
        },
      });
      const rows = resp?.data?.data || [];
      setShifts(rows);
      setInitialShifts(rows);
    } catch (e) {
      console.error("Failed to load similar shifts", e);
      showError(e?.response?.data?.message || "Failed to load similar shifts");
      setShifts([]);
      setInitialShifts([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, positionId, rangeFrom, rangeTo, originalAccountId]);

  useEffect(() => {
    if (!open) return;
    const week = isoWeekRangeForShift(shift);
    setRangeFrom(week.from);
    setRangeTo(week.to);
    setShifts([]);
    setInitialShifts([]);
    setRowBusyId(null);
    setSaving(false);
    setShowDateRangeModal(false);
  }, [open, shift]);

  useEffect(() => {
    if (!open || !originalAccountId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await API.get(`/api/personnel/accounts/${originalAccountId}`);
        const data = resp?.data?.data || resp?.data || null;
        if (!cancelled) setWorker(data);
      } catch {
        if (!cancelled) setWorker(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, originalAccountId]);

  useEffect(() => {
    if (!open) return;
    if (clientId && positionId && rangeFrom && rangeTo && originalAccountId) {
      loadShifts();
    }
  }, [open, clientId, positionId, rangeFrom, rangeTo, originalAccountId, loadShifts]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !showDateRangeModal) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, showDateRangeModal]);

  const handleAssignSwitch = (row, nextChecked) => {
    const shiftId = row.shift_id;
    if (!shiftId || !originalAccountId) return;
    setShifts((prev) =>
      prev.map((item) =>
        item.shift_id === shiftId
          ? {
              ...item,
              assigned: nextChecked,
              confirmed: nextChecked ? !!item.confirmed : false,
              account_id: nextChecked ? originalAccountId : null,
            }
          : item,
      ),
    );
  };

  const handleConfirmToggle = (row, nextConfirmed) => {
    const shiftId = row.shift_id;
    if (!shiftId || !originalAccountId || !row.assigned) return;
    setShifts((prev) =>
      prev.map((item) =>
        item.shift_id === shiftId
          ? {
              ...item,
              confirmed: nextConfirmed,
            }
          : item,
      ),
    );
  };

  const handleDone = async () => {
    if (saving) return;
    const initialById = new Map(initialShifts.map((row) => [String(row.shift_id), row]));
    const changedRows = shifts.filter((row) => {
      const initial = initialById.get(String(row.shift_id));
      if (!initial) return false;
      return (
        !!row.assigned !== !!initial.assigned ||
        !!row.confirmed !== !!initial.confirmed
      );
    });

    if (changedRows.length === 0) {
      onDone?.();
      onClose();
      return;
    }

    setSaving(true);
    try {
      for (const row of changedRows) {
        const shiftId = row.shift_id;
        const initial = initialById.get(String(shiftId));
        if (!shiftId || !initial) continue;

        if (!!row.assigned !== !!initial.assigned) {
          if (row.assigned) {
            const assignResp = await fetch(`${BASE_URL}/api/shifts/${shiftId}/assign`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...getAuthHeaders(),
              },
              body: JSON.stringify({ accountId: originalAccountId }),
            });
            const assignJson = await assignResp.json().catch(() => ({}));
            if (!assignResp.ok) {
              throw new Error(assignJson.message || "Failed to assign staff");
            }
          } else {
            const unResp = await fetch(
              `${BASE_URL}/api/shifts/${shiftId}/force-unassign`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  ...getAuthHeaders(),
                },
                body: JSON.stringify({
                  accountId: originalAccountId,
                  affectsReliability: false,
                }),
              },
            );
            const unJson = await unResp.json().catch(() => ({}));
            if (!unResp.ok) {
              throw new Error(unJson.message || "Failed to unassign staff");
            }
          }
        }

        if (row.assigned && !!row.confirmed !== !!initial.confirmed) {
          const resp = await fetch(
            `${BASE_URL}/api/shifts/${shiftId}/confirm-assignment`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                ...getAuthHeaders(),
              },
              body: JSON.stringify({
                accountId: originalAccountId,
                state: row.confirmed ? "confirmed" : "pending",
              }),
            },
          );
          const json = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            throw new Error(json.message || "Failed to update confirmation");
          }
        }
      }

      onDone?.();
      onClose();
    } catch (e) {
      showError(e.message || "Failed to save similar shift changes");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const modal = (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-40 z-[50000] flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget && !saving) onClose();
        }}
      >
        <div
          className="bg-white rounded shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col no-mobile-table-stack"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 overflow-y-auto flex-1">
            <h3 className="text-lg font-semibold mb-4">Assign to Similar</h3>

            <h4 className="text-sm font-semibold text-gray-800 mb-3">
              Original Shift Assignment
            </h4>
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-3">
                <StaffAvatarCircle
                  employee={worker}
                  accountId={originalAccountId}
                  className="w-12 h-12 flex-shrink-0"
                  textClassName="text-base font-medium text-gray-700"
                  emptyLabel="?"
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{employeeName}</div>
                  <div className="text-xs text-gray-500">
                    {sanitizePhone(
                      worker?.phone_mobile || worker?.phone || shift.phone,
                    )}
                  </div>
                </div>
              </div>
              <div className="text-sm">
                <div className="font-semibold text-base mb-1">
                  {clientName || "—"} • {positionName || "—"}
                </div>
                <div className="text-gray-600">
                  {formatDisplayRange(shift.start_date || shift.startDate, shift.end_date || shift.endDate)}
                </div>
                {durationText ? (
                  <div className="text-gray-600">{durationText}</div>
                ) : null}
                <div className="text-gray-600">
                  Supervisor: {supervisorName || "—"}
                </div>
                <div className="text-gray-600">Pay Rate: {formatMoney(payRate)}</div>
                <div className="text-gray-600">Bill Rate: {formatMoney(billRate)}</div>
              </div>
            </div>

            <h4 className="text-sm font-semibold text-gray-800 mb-3">
              Assign {employeeName} to similar shifts for {clientName || "client"}
            </h4>

            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700 border">
                Start Date: {rangeFrom || "—"}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-gray-100 text-gray-700 border">
                End Date: {rangeTo || "—"}
              </span>
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => setShowDateRangeModal(true)}
                className="px-3 py-1.5 border rounded text-sm font-medium hover:bg-gray-50"
              >
                Date Range
              </button>
            </div>

            <div className="border rounded overflow-y-auto overflow-x-auto" style={{ maxHeight: 320 }}>
              {loading ? (
                <div className="p-4 text-sm text-gray-600">Loading...</div>
              ) : visibleShifts.length === 0 ? (
                <div className="p-4 text-sm text-gray-600">
                  No similar shifts found for this date range.
                </div>
              ) : (
                <table className="w-full min-w-[620px] text-sm">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="text-left text-xs text-gray-500 border-b">
                      <th className="p-2 w-10" />
                      <th className="p-2">Shift</th>
                      <th className="p-2">Assigned Agency</th>
                      <th className="p-2 w-24 text-center">Confirmed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleShifts.map((row) => {
                      const busy = rowBusyId === row.shift_id;
                      return (
                        <tr key={row.shift_id} className="border-b border-gray-100">
                          <td className="p-2 align-middle">
                            <input
                              type="checkbox"
                              checked={!!row.assigned}
                              disabled={busy || loading || saving}
                              onChange={(e) =>
                                handleAssignSwitch(row, e.target.checked)
                              }
                            />
                          </td>
                          <td className="p-2 align-middle text-gray-800">
                            {formatDisplayRange(row.start_date, row.end_date)}
                          </td>
                          <td className="p-2 align-middle text-gray-600">
                            {row.assigned_agency || "None"}
                          </td>
                          <td className="p-2 align-middle text-center">
                            {row.assigned ? (
                              <input
                                type="checkbox"
                                checked={!!row.confirmed}
                                disabled={busy || loading || saving}
                                onChange={(e) =>
                                  handleConfirmToggle(row, e.target.checked)
                                }
                              />
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {hiddenOverlapCount > 0 ? (
              <p className="text-xs text-gray-500 mt-3">
                {hiddenOverlapCount} overlapping shift
                {hiddenOverlapCount === 1 ? "" : "s"} hidden because a selected
                shift already covers that time.
              </p>
            ) : null}

            {hiddenBookedElsewhereCount > 0 ? (
              <p className="text-xs text-gray-500 mt-3">
                {hiddenBookedElsewhereCount} shift
                {hiddenBookedElsewhereCount === 1 ? "" : "s"} hidden because{" "}
                {employeeName} is already booked on another shift at that time.
              </p>
            ) : null}

            {hiddenOtherStaffCount > 0 ? (
              <p className="text-xs text-gray-500 mt-3">
                {hiddenOtherStaffCount} similar shift
                {hiddenOtherStaffCount === 1 ? "" : "s"} already assigned to other
                staff hidden to avoid replacement.
              </p>
            ) : null}

            {hiddenCapacityCount > 0 ? (
              <p className="text-xs text-gray-500 mt-3">
                {hiddenCapacityCount} full shift
                {hiddenCapacityCount === 1 ? "" : "s"} hidden because all slots
                are already filled.
              </p>
            ) : null}

            <p className="text-xs text-gray-500 mt-3">
              Shifts unassigned here will not be counted towards reliability
            </p>
          </div>

          <div className="border-t px-6 py-4 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-5 py-2 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDone}
              disabled={saving || loading}
              className="px-5 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "Done"}
            </button>
          </div>
        </div>
      </div>

      <AssignToSimilarDateRangeModal
        open={showDateRangeModal}
        dateFrom={rangeFrom}
        dateTo={rangeTo}
        onClose={() => setShowDateRangeModal(false)}
        onApply={({ dateFrom, dateTo }) => {
          setRangeFrom(dateFrom);
          setRangeTo(dateTo);
          setShowDateRangeModal(false);
        }}
      />
    </>
  );

  if (typeof document !== "undefined" && ReactDOM?.createPortal) {
    return ReactDOM.createPortal(modal, document.body);
  }
  return modal;
}
